import { useState, type ReactNode } from 'react';
import { splitPrompt } from '@shared/rules';
import type { CardData } from '@shared/types';
import { canSpeak, speakCard } from '../lib/speak';
import { usePrefs } from './Prefs';
import { Sheet } from './Sheet';

/** The (i) button: background on a film, custom or person, for players who didn't grow up with it. */
function InfoButton({ card }: { card: CardData }) {
  const [open, setOpen] = useState(false);
  if (!card.explanation) return null;
  return (
    <>
      <span
        role="button"
        tabIndex={0}
        className="info"
        aria-label="What does this mean?"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen(true);
          }
        }}
      >
        i
      </span>
      {open && (
        <Sheet title="Samjhao" onClose={() => setOpen(false)}>
          <p style={{ fontWeight: 800, fontSize: '1.1rem' }}>{card.text}</p>
          {card.gloss && <p className="muted" style={{ fontStyle: 'italic' }}>{card.gloss}</p>}
          <p>{card.explanation}</p>
        </Sheet>
      )}
    </>
  );
}

function Gloss({ card }: { card: CardData }) {
  const { showGloss, showDevanagari } = usePrefs();
  return (
    <>
      {showDevanagari && card.devanagari && (
        <span className="deva" lang="hi">
          {card.devanagari}
        </span>
      )}
      {showGloss && card.gloss && <span className="gloss">{card.gloss}</span>}
    </>
  );
}

function Photo({ card }: { card: CardData }) {
  if (!card.imageUrl) return null;
  return <img className="card-photo" src={card.imageUrl} alt="" loading="lazy" />;
}

/** Read the prompt out loud, for judges who don't want to butcher the Hindi and elders with small screens. */
function SpeakButton({ card, answers }: { card: CardData; answers?: string[] }) {
  if (!canSpeak()) return null;
  return (
    <span
      role="button"
      tabIndex={0}
      className="speak"
      aria-label="Read this card out loud"
      onClick={(e) => {
        e.stopPropagation();
        speakCard(card, answers);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          speakCard(card, answers);
        }
      }}
    >
      🔊
    </span>
  );
}

/** A prompt card, optionally with answers dropped into the blanks. */
export function PromptCard({ card, answers, className = '' }: { card: CardData; answers?: string[]; className?: string }) {
  const parts = splitPrompt(card, answers);
  const hasBlank = parts.some((p) => p.kind !== 'text');
  return (
    <div className={`card prompt ${className}`}>
      <Photo card={card} />
      <span>
        {parts.map((p, i) =>
          p.kind === 'text' ? (
            <span key={i}>{p.value}</span>
          ) : p.kind === 'fill' ? (
            <span key={i} className="fill">
              {p.value}
            </span>
          ) : (
            <span key={i} className="blank" aria-label="blank" />
          ),
        )}
        {!hasBlank && answers?.length ? (
          <>
            {' '}
            <span className="fill">{answers.join(', ')}</span>
          </>
        ) : null}
      </span>
      {!answers?.length && <Gloss card={card} />}
      <span className="brandmark">CompleteMasti</span>
      {card.pick > 1 && <span className="pick">PICK {card.pick}</span>}
      <SpeakButton card={card} answers={answers} />
      <InfoButton card={card} />
    </div>
  );
}

interface AnswerCardProps {
  card: CardData;
  selected?: boolean;
  order?: number;
  onClick?: () => void;
  disabled?: boolean;
  className?: string;
  footer?: ReactNode;
}

export function AnswerCard({ card, selected, order, onClick, disabled, className = '', footer }: AnswerCardProps) {
  const classes = `card answer ${selected ? 'selected' : ''} ${card.writeIn ? 'writein' : ''} ${className}`;
  const body = (
    <>
      <Photo card={card} />
      {card.text}
      <Gloss card={card} />
      <span className="brandmark">{card.writeIn ? 'Written in' : 'CompleteMasti'}</span>
      {order ? <span className="order">{order}</span> : null}
      <InfoButton card={card} />
      {footer}
    </>
  );
  if (!onClick) return <div className={classes}>{body}</div>;
  return (
    <button type="button" className={classes} onClick={onClick} disabled={disabled} aria-pressed={selected}>
      {body}
    </button>
  );
}

export function FaceDownCard({ onClick, label = 'Tap to flip' }: { onClick?: () => void; label?: string }) {
  return (
    <button type="button" className="card facedown" onClick={onClick} disabled={!onClick} aria-label={label}>
      <img src="/logo-mark.png" alt="" />
      {onClick && <span className="tap">{label}</span>}
    </button>
  );
}

/** A prompt with answers filled in, as one line of text (for recaps and round results). */
export function FilledPrompt({ prompt, answers }: { prompt: CardData; answers: string[] }) {
  const parts = splitPrompt(prompt, answers);
  const hasBlank = parts.some((p) => p.kind !== 'text');
  return (
    <div className="filled-prompt">
      {parts.map((p, i) =>
        p.kind === 'text' ? (
          <span key={i}>{p.value}</span>
        ) : (
          <mark key={i} className="hl">
            {p.kind === 'fill' ? p.value : '___'}
          </mark>
        ),
      )}
      {!hasBlank && (
        <>
          {' '}
          <mark className="hl">{answers.join(', ')}</mark>
        </>
      )}
    </div>
  );
}
